import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { getUserCredits, grantCredits } from "../../api/billing";
import { formatDateTime } from "../../shared/utils/format";
import { DataTable, LoadState, Pagination, SectionCard, type Column } from "../UserDetails/ui";
import { UserPicker } from "./UserPicker";
import type { CreditLedgerItem, UserSummary } from "./types";
import { Chip, ErrorText, Field, SaveButton, inputClass, useAction, useLoad } from "./ui";

const MAX_GRANT = 100_000;

const ENTRY_TONE: Record<string, "emerald" | "red" | "gray" | "amber"> = {
  GRANT: "emerald",
  DEBIT: "red",
  EXPIRATION: "gray",
  ADJUSTMENT: "amber",
};

const columns: Column<CreditLedgerItem>[] = [
  { header: "When", render: (r) => formatDateTime(r.createdAt), className: "whitespace-nowrap" },
  { header: "Type", render: (r) => <Chip tone={ENTRY_TONE[r.entryType] ?? "gray"}>{r.entryType.toLowerCase()}</Chip> },
  { header: "Amount", render: (r) => (r.entryType === "GRANT" || r.entryType === "ADJUSTMENT" ? `+${r.amount}` : `−${r.amount}`) },
  { header: "Bucket", render: (r) => r.bucket.toLowerCase().replaceAll("_", " ") },
  { header: "Source", render: (r) => r.source.toLowerCase() },
  {
    header: "Note",
    render: (r) => (typeof r.metadata?.note === "string" ? r.metadata.note : "—"),
  },
];

const UserLedger: React.FC<Readonly<{ userId: number }>> = ({ userId }) => {
  const [page, setPage] = useState(1);
  const load = useCallback(() => getUserCredits(userId, page), [userId, page]);
  const { data, loading, error, reload } = useLoad(load);

  if ((loading && !data) || error) return <LoadState loading={loading && !data} error={error} onRetry={reload} />;
  if (!data) return null;
  const { summary } = data;
  const tiles: Array<[string, number | null]> = [
    ["Available now", summary.available],
    ["Granted", summary.granted],
    ["Spent", summary.spent],
    ["Expired", summary.expired],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-lg font-semibold text-gray-900">{value ?? "—"}</p>
          </div>
        ))}
      </div>
      <DataTable columns={columns} rows={data.items} emptyText="No credit activity yet." />
      <Pagination page={data.page} totalPages={data.totalPages} totalCount={data.totalCount} onChange={setPage} />
    </div>
  );
};

/** Grant DrawPost credits to one user and see their ledger. */
export const CreditsTab: React.FC = () => {
  const [user, setUser] = useState<UserSummary | null>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [version, setVersion] = useState(0);
  const [granted, setGranted] = useState<string | null>(null);
  const { busy, error, setError, run } = useAction();

  const grant = async () => {
    setGranted(null);
    const value = Number.parseInt(amount, 10);
    if (!user) return setError("Pick a user.");
    if (!Number.isInteger(value) || value < 1 || value > MAX_GRANT) return setError(`Amount must be 1–${MAX_GRANT}.`);
    if (!note.trim()) return setError("Add a short reason — it is saved with the grant.");
    const done = await run(() => grantCredits({ userId: user.id, amount: value, note: note.trim() }));
    if (done) {
      setGranted(`${value} credits granted to ${user.email}.`);
      setAmount("");
      setNote("");
      setVersion((v) => v + 1);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Grant DrawPost credits">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.5fr_0.6fr_1.5fr_auto] md:items-end">
          <Field label="User">
            <UserPicker
              value={user}
              disabled={busy}
              onChange={(u) => {
                setUser(u);
                setGranted(null);
              }}
            />
          </Field>
          <Field label="Credits">
            <input type="number" min={1} max={MAX_GRANT} className={inputClass} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Reason">
            <input className={inputClass} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. compensation for outage" />
          </Field>
          <SaveButton busy={busy} onClick={grant}>
            Grant
          </SaveButton>
        </div>
        <p className="mt-2 text-xs text-gray-400">Granted credits are added as promo credits and never expire.</p>
        <div className="mt-2">
          <ErrorText message={error} />
          {granted && <p className="text-sm text-emerald-700">{granted}</p>}
        </div>
      </SectionCard>

      {user && (
        <SectionCard
          title={`Credits · ${user.email}`}
          action={
            <Link to={`/users/${user.id}`} className="text-xs font-medium text-violet-700 hover:underline">
              Open user
            </Link>
          }
        >
          {/* Remounted after each grant so the new entry shows. */}
          <UserLedger key={`${user.id}-${version}`} userId={user.id} />
        </SectionCard>
      )}
    </div>
  );
};
